import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// High-contrast black & white styles for HTML elements in the clone.
// (SVG fill/stroke are handled imperatively below — html2canvas does not apply
// stylesheet rules to SVG presentational attributes.)
const BW_CSS = `
.pdf-export-bw {
  background:#ffffff !important;
  opacity:1 !important;
  animation:none !important;
  transform:none !important;
  filter:none !important;
}
.pdf-export-bw * {
  color:#000000 !important;
  border-color:#555555 !important;
  box-shadow:none !important;
  text-shadow:none !important;
  background-image:none !important;
  opacity:1 !important;
}
.pdf-export-bw *:not(svg):not(path):not(g):not(circle):not(rect):not(line):not(text):not(tspan):not(polyline) {
  background-color:transparent !important;
}
`;

const EMOJI_RE = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}\u{FE0F}\u{1F1E6}-\u{1F1FF}]/gu;

function blackenSvgAndStripEmoji(root, doc) {
  // html2canvas inlines getComputedStyle() from the original DOM onto the clone's
  // `style` attribute before serializing SVG, which OVERRIDES setAttribute("stroke"/"fill").
  // We must therefore set BOTH the SVG presentational attribute AND the inline CSS style
  // (style wins over attribute in SVG cascade).
  const forceBlack = (el, { fill = true, stroke = true } = {}) => {
    if (fill) {
      const f = el.getAttribute("fill");
      if (f !== "none") {
        el.setAttribute("fill", "#000000");
        try { el.style.fill = "#000000"; } catch (_) {}
      }
    }
    if (stroke) {
      const s = el.getAttribute("stroke");
      if (s !== "none") {
        el.setAttribute("stroke", "#000000");
        try { el.style.stroke = "#000000"; } catch (_) {}
      }
    }
  };
  // Text labels/ticks — always want black fill
  root.querySelectorAll("svg text, svg tspan").forEach((t) => {
    t.setAttribute("fill", "#000000");
    try { t.style.fill = "#000000"; } catch (_) {}
  });
  // Data-point dots
  root.querySelectorAll("svg circle").forEach((c) => forceBlack(c));
  // Lines / legend swatches (preserve fill:none / stroke:none)
  root.querySelectorAll("svg path, svg line, svg polyline").forEach((p) => forceBlack(p));
  // Rects (bar charts, backgrounds inside SVG)
  root.querySelectorAll("svg rect").forEach((r) => forceBlack(r));
  // Grid lines stay light gray for readability
  root.querySelectorAll(".recharts-cartesian-grid line").forEach((l) => {
    l.setAttribute("stroke", "#bbbbbb");
    try { l.style.stroke = "#bbbbbb"; } catch (_) {}
  });

  // Remove colour-emoji glyphs (cannot be recoloured by CSS)
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach((n) => {
    const cleaned = n.nodeValue.replace(EMOJI_RE, "");
    if (cleaned !== n.nodeValue) n.nodeValue = cleaned;
  });
}

// Renders a DOM element to a multi-page A4 PDF in readable BLACK & WHITE.
// Elements with the class "no-export" (e.g. control buttons) are skipped.
export async function exportElementToPdf(element, filename) {
  element.setAttribute("data-pdf-root", "1");

  let canvas;
  try {
    canvas = await html2canvas(element, {
      scale: 3,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      ignoreElements: (el) => el.classList && el.classList.contains("no-export"),
      onclone: (doc) => {
        const style = doc.createElement("style");
        style.textContent = BW_CSS;
        doc.head.appendChild(style);
        const root = doc.querySelector('[data-pdf-root="1"]');
        if (root) {
          root.classList.add("pdf-export-bw");
          blackenSvgAndStripEmoji(root, doc);
        }
      },
    });
  } finally {
    element.removeAttribute("data-pdf-root");
  }

  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const margin = 24;
  const imgWidth = pageWidth - margin * 2;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = margin;

  pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
  heightLeft -= pageHeight - margin * 2;

  while (heightLeft > 0) {
    pdf.addPage();
    position = margin - (imgHeight - heightLeft);
    pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
    heightLeft -= pageHeight - margin * 2;
  }

  pdf.save(filename);
}
